import type { FC, ReactNode } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import type { EmojiDescription } from '@atlaskit/emoji/types';
import { token } from '@atlaskit/tokens';
import { Box, xcss } from '@atlaskit/primitives';

import { DocumentSpaceTitle } from '@confluence/document-title';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import {
	EMOJI_SIZE_MEDIUM,
	EMOJI_SIZE_LARGE,
} from '@confluence/emoji-title/entry-points/constants';
import { Breadcrumbs, FixedSpaceHeaderContainer } from '@confluence/breadcrumbs';
import { useRouteDataRef } from '@confluence/route-manager';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { ContentClassificationTag } from '@confluence/data-classification';
import { ContentAppearanceType } from '@confluence/content-appearance';
import {
	useContentClassification,
	useDefaultAndSpaceClassificationLevels,
} from '@confluence/data-classification/entry-points/queries';
import { useCreateSpaceExperiment } from '@confluence/create-space/entry-points/createSpace';
import { COVER_IMAGE_FIXED_WIDTH } from '@confluence/page-cover-picture';
import { fg } from '@confluence/feature-gating';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceTitleContainer = styled.div<{
	contentAppearance: any;
	isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ contentAppearance, isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		contentAppearance !== ContentAppearanceType.DEFAULT
			? isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji
				? { display: 'flex' }
				: {}
			: {
					margin: `0 auto ${token('space.300', '24px')}`,
					wordBreak: 'break-word',
					wordWrap: 'break-word',
					maxWidth: '760px',
				},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const SpaceHeaderText = styled.h1<{
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	hasIcon?: boolean;
	spaceIconPath?: string;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ hasCoverPicture, isTitleCenterAligned, hasIcon }) =>
		hasIcon && !isTitleCenterAligned && !hasCoverPicture ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ hasCoverPicture, hasIcon }) =>
		hasCoverPicture && hasIcon ? '-56px !important' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'flex' : 'inline-flex'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned || hasCoverPicture ? 'column' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'intial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textAlign: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	flex: 1,
	wordBreak: 'break-word',
	wordWrap: 'break-word',
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: ({ isTitleCenterAligned }) =>
		isTitleCenterAligned ? 'initial' : token('space.250', '20px'),
	font: token('font.heading.xlarge'),
	fontWeight: token('font.weight.regular'),
	lineHeight: 1.25,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ hasCoverPicture, isTitleCenterAligned }) => ({
		gap: hasCoverPicture
			? token('space.300', '24px')
			: isTitleCenterAligned
				? token('space.200', '16px')
				: token('space.150', '12px'),
	}),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapper = styled.span({
	display: 'inline-flex',
	flex: 0,
	marginRight: token('space.075', '6px'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBottom: '1px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapperWithAlignment = styled(EmojiWrapper)({
	marginRight: token('space.0', '0px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconStyleWrapper = styled.span<{
	isTitleCenterAligned?: boolean;
	isFixedWidthImageOption?: boolean;
	isPageContentFullWidth?: boolean;
	hasCoverPicture?: boolean;
	isCompanyHub?: boolean;
}>({
	display: 'flex',
	alignItems: 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ isFixedWidthImageOption, hasCoverPicture, isTitleCenterAligned }) =>
		isFixedWidthImageOption && hasCoverPicture && !isTitleCenterAligned
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${COVER_IMAGE_FIXED_WIDTH}px`
			: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: ({ isFixedWidthImageOption, hasCoverPicture, isTitleCenterAligned }) =>
		isFixedWidthImageOption && hasCoverPicture && !isTitleCenterAligned ? '0 auto' : 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ isCompanyHub, hasCoverPicture }) =>
		!hasCoverPicture && isCompanyHub ? token('space.150', '12px') : 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({
		isFixedWidthImageOption,
		hasCoverPicture,
		isTitleCenterAligned,
		isPageContentFullWidth,
	}) =>
		isFixedWidthImageOption && hasCoverPicture && !isTitleCenterAligned && isPageContentFullWidth
			? token('space.800', '64px')
			: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceIcon = styled.img({
	borderRadius: '8px',
});

const BREADCRUMBS_MIN_WIDTH = '150px';

const breadcrumbsWrapper = xcss({
	minWidth: BREADCRUMBS_MIN_WIDTH, // needed for responsive breadcrumbs
});

const noWrapStyles = xcss({
	display: 'flex',
	flexWrap: 'nowrap',
});

interface SpaceHeaderProps {
	children?: ReactNode;
	spaceKey: string;
	isThemed?: boolean;
	spaceIconPath?: string;
	contentId?: string;
}

type SpaceHeaderTextProps = {
	title: ReactNode | string;
	emoji: string | EmojiDescription | null;
	isTitleCenterAligned?: boolean;
	contentAppearance?: string;
	hasCoverPicture?: boolean;
	spaceIconPath?: string;
	isCompanyHub?: boolean;
	isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji?: boolean;
	isPageContentFullWidth?: boolean;
	isFixedWidthImageOption?: boolean;
};

export const SpaceHeading = ({
	emoji,
	title,
	isTitleCenterAligned,
	isCompanyHub,
	contentAppearance,
	hasCoverPicture,
	spaceIconPath,
	isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji,
	isPageContentFullWidth,
	isFixedWidthImageOption,
}: SpaceHeaderTextProps) => {
	const emojiHeight = () => {
		if (hasCoverPicture) {
			return EMOJI_SIZE_LARGE;
		} else {
			return EMOJI_SIZE_MEDIUM;
		}
	};

	const hasEmoji = Boolean(emoji);

	const { isSpaceTypesCreationFlowEnabled } = useCreateSpaceExperiment();
	const isSpaceIconEnabled = isSpaceTypesCreationFlowEnabled && !!spaceIconPath;

	return (
		<SpaceTitleContainer
			contentAppearance={contentAppearance}
			isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji={
				isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji
			}
		>
			<SpaceHeaderText
				isTitleCenterAligned={isTitleCenterAligned}
				hasCoverPicture={hasCoverPicture}
				hasIcon={hasEmoji || isSpaceIconEnabled}
				data-test-id="title-text"
				id="title-text"
				spaceIconPath={spaceIconPath}
			>
				{hasEmoji || isSpaceIconEnabled ? (
					<IconStyleWrapper
						isTitleCenterAligned={isTitleCenterAligned}
						isFixedWidthImageOption={isFixedWidthImageOption}
						isPageContentFullWidth={isPageContentFullWidth}
						isCompanyHub={isCompanyHub}
						hasCoverPicture={hasCoverPicture}
						data-testid="emoji"
					>
						{hasEmoji ? (
							<LazyEmojiComponentLoader
								emoji={emoji}
								context="spaceHeader"
								// TODO: update this to use size constants
								height={emojiHeight()}
								wrapper={EmojiWrapperWithAlignment}
								renderResourcedEmoji
								isPageTitleComponent
							/>
						) : (
							<SpaceIcon
								data-vc="space-header-icon"
								width={emojiHeight()}
								height={emojiHeight()}
								src={`${CONTEXT_PATH}${spaceIconPath}`}
							/>
						)}
					</IconStyleWrapper>
				) : null}
				{title}
			</SpaceHeaderText>
		</SpaceTitleContainer>
	);
};

export const SpaceHeader: FC<SpaceHeaderProps> = ({ children, spaceKey, isThemed, contentId }) => {
	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: 'current-only',
	});

	const spaceClassificationMetadata = useDefaultAndSpaceClassificationLevels(spaceKey);

	const routeDataRef = useRouteDataRef();
	// We are expanding atl.general panel to full width when the side nav is not present. This is the case for
	// company hub and when url has mode=global, such as on custom homepage
	// TODO: For Nav 4, we want to replace these checks so that we simply check for existence of the side nav (DISCO-2142)
	const isFullWidth = routeDataRef.current?.queryParams?.mode === 'global';

	return (
		<FixedSpaceHeaderContainer isThemed={isThemed} isFullWidth={isFullWidth}>
			<DocumentSpaceTitle spaceKey={spaceKey} />
			<Box
				xcss={[breadcrumbsWrapper, fg('confluence_modernized_breadcrumbs') && noWrapStyles]}
				testId="breadcrumbs-and-classification-tag"
			>
				<Breadcrumbs spaceKey={spaceKey} />
				<div data-testid="content-classification-tag">
					<ContentClassificationTag
						contentId={contentId ?? ''}
						contentType="page"
						customSitesTitleComponent
						contentClassificationMetadata={contentClassificationMetadata}
						spaceClassificationMetadata={spaceClassificationMetadata}
						isEditMode={false}
					/>
				</div>
			</Box>

			{children}
		</FixedSpaceHeaderContainer>
	);
};
